import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import { styles } from './DeleteDialog.styles';

const intl = {
  label: 'Type "DELETE" to confirm',
  confirmString: 'DELETE',
  cancel: 'Cancel',
  delete: 'Delete',
  closeIconAriaLabel: 'close button for dialog'
};

interface DeleteDialogProps {
  title: string;
  isDeleting?: boolean;
  dataTest?: string;
  children?: React.ReactNode;
  onClose: () => void;
  onDelete: () => void;
  dataTestInput?: string;
  helperText?: string | JSX.Element;
  dataTestHeader?: string;
  dataTestConfirmBtn?: string;
  shouldRequireConfirmationDeleteText?: boolean;
}

export function DeleteDialog({
  title,
  isDeleting = false,
  dataTest = 'deleteDialog',
  children,
  onClose,
  onDelete,
  dataTestInput,
  helperText,
  dataTestHeader,
  dataTestConfirmBtn,
  shouldRequireConfirmationDeleteText = true
}: Readonly<DeleteDialogProps>) {
  const [confirmation, setConfirmation] = useState('');
  const needsConfirmation = shouldRequireConfirmationDeleteText && confirmation !== intl.confirmString;

  return (
    <Dialog open={true} onClose={onClose} fullWidth data-test={dataTest}>
      <IconButton className="MuiDialog__close-button" aria-label={intl.closeIconAriaLabel} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <DialogTitle color="error.main" data-test={dataTestHeader}>
        <ErrorIcon />
        {title}
      </DialogTitle>

      <DialogContent sx={styles.content}>
        {children ? <DialogContentText component="div">{children}</DialogContentText> : null}
        {shouldRequireConfirmationDeleteText ? (
          <TextField
            fullWidth={true}
            label={intl.label}
            InputLabelProps={{ sx: { userSelect: 'text' } }}
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !needsConfirmation) {
                onDelete();
              }
            }}
            inputProps={{
              className: 'no-format',
              'data-test': dataTestInput ?? 'confirmationInput'
            }}
            helperText={helperText}
          />
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button disabled={isDeleting} color="secondary" data-test="cancelDeleteButton" onClick={onClose}>
          {intl.cancel}
        </Button>
        <LoadingButton
          loading={isDeleting}
          disabled={needsConfirmation}
          color="error"
          variant="contained"
          data-test={dataTestConfirmBtn ?? 'deleteButton'}
          onClick={onDelete}
        >
          {intl.delete}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
